import type { CalendarEventContextMenuViewState } from 'owa-calendar-event-context-menu';
import { openItemPeek } from 'owa-calendar-peek';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { ClientItemId } from 'owa-client-ids';
import type EventScope from 'owa-service/lib/contract/EventScope';
import { useGetCalendarEvents } from 'owa-timestream-asset-sdk-calendarevents';
import { useCalendarAssetCallbacks } from 'owa-timestream-asset-sdk-calendars';
import * as React from 'react';

export function useContextMenuState(setPeekTarget: (target: HTMLElement) => void) {
    const [activeItem, setActiveItem] = React.useState<HTMLElement>();
    const [calendarEventIdForDetails, setCalendarEventIdForDetails] = React.useState<string | null>(
        null
    );
    const { getCalendarEvent } = useGetCalendarEvents();

    const onOpenEventDetails = React.useCallback(
        (id: ClientItemId) => {
            if (activeItem && id) {
                setCalendarEventIdForDetails(id.Id);
            }
        },
        [activeItem]
    );

    const { onOpenEdit } = useCalendarAssetCallbacks();
    const onEditEvent = React.useCallback(
        (event: CalendarEvent, entrySource: string, eventScope: EventScope) => {
            onOpenEdit(event, false /** eventHasUpdates */, eventScope);
        },
        [onOpenEdit]
    );

    const calendarEvent = calendarEventIdForDetails
        ? getCalendarEvent(calendarEventIdForDetails, () => {})
        : null;

    React.useEffect(() => {
        if (calendarEvent && activeItem) {
            setPeekTarget(activeItem);
            // initialize the actual peek since context menu doesn't by default, despite initializing the response notes
            openItemPeek.importAndExecute(calendarEvent);
        }
    }, [calendarEvent, activeItem, setPeekTarget]);

    const [
        contextMenuViewState,
        setContextMenuViewState,
    ] = React.useState<CalendarEventContextMenuViewState | null>(null);
    const onRightClickCalendarEvent = React.useCallback(
        (
            itemId: ClientItemId,
            parentFolderId: string,
            clickEvent: React.MouseEvent<HTMLDivElement>
        ) => {
            setContextMenuViewState({ itemId, parentFolderId, clickEvent });
            setCalendarEventIdForDetails(null);
            setActiveItem(clickEvent.currentTarget);
        },
        []
    );

    const onCloseContextMenu = React.useCallback(() => {
        setContextMenuViewState(null);
    }, []);

    return {
        onOpenEventDetails,
        onEditEvent,
        onCloseContextMenu,
        onContextMenuInvoked: onRightClickCalendarEvent,
        contextMenuOpen: !!contextMenuViewState,
        contextMenuViewState,
    };
}
